import { useCallback } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { filesApi as filesApiBase } from "src/api/endpoints";
import { getFileEnvAgnostic } from "src/api/filesApi/filesApi";
import {
  FileUploadDataTypeEnum,
  FileUploadRequest,
  GetFileEnvFilesFileIdGetEnvEnum,
  GetFilesEnvFilesGetEnvEnum,
  JsonApiResourceObjectFileAttributesFileMetaData,
  UploadFileEnvFilesPostEnvEnum,
} from "src/clients/files";
import { toastActions } from "src/design-system/Toast/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  downloadFileFromUrl,
  FileSource,
  PLACEHOLDER_FILE_ID,
} from "src/utils/files";

export const FILES_QUERY_KEY = ["uploaded-file"];

const ENV_AGNOSTIC_KEY = "env-agnostic" as const;

const fileKeys = {
  all: (
    env: GetFileEnvFilesFileIdGetEnvEnum | typeof ENV_AGNOSTIC_KEY,
    baseUrl?: string,
  ) => ["files", baseUrl, env],
  file: (
    env: GetFileEnvFilesFileIdGetEnvEnum | typeof ENV_AGNOSTIC_KEY,
    fileId: string | undefined,
    baseUrl?: string,
  ) => [...fileKeys.all(env, baseUrl), fileId],
  list: (
    env: GetFilesEnvFilesGetEnvEnum | undefined,
    fileIds: string[],
    baseUrl?: string,
  ) => ["files", baseUrl, env, "list", fileIds],
  uploadedFile: (key: string | undefined) =>
    key ? [...FILES_QUERY_KEY, key] : FILES_QUERY_KEY,
};

// The Files API returns S3 presigned download/upload URLs that expire after a
// short TTL on the backend (`URL_EXPIRATION_TIME = 5 * 60` in
// workspace-services/modules/files/src/manager/base.py). When a case is left
// open in an idle tab, the cached link goes stale and the next file open or
// download hits a 403. Refetch the file metadata — and with it a fresh
// presigned URL — in the background, comfortably before the backend TTL, so an
// idle tab never uses an expired link. See ENR-17870.
const FILE_URL_REFRESH_INTERVAL_MS = 4 * 60 * 1000; // 4 min, under the 5 min backend TTL

export type UseFileOptions = {
  /** Sandbox-then-live lookup for historical test-run UIs and test datasets. */
  envAgnostic?: boolean;
};

export type FileMetadata = JsonApiResourceObjectFileAttributesFileMetaData;

type FileQueryParams = {
  workspaceBaseUrl: string | undefined;
  fileId: string | undefined;
  environment: GetFileEnvFilesFileIdGetEnvEnum | undefined;
  baseUrl: string | undefined;
  envAgnostic: boolean;
};

const fileQueryOptions = ({
  workspaceBaseUrl,
  fileId,
  environment,
  baseUrl,
  envAgnostic,
}: FileQueryParams) => ({
  queryKey: fileKeys.file(
    envAgnostic ? ENV_AGNOSTIC_KEY : environment!,
    fileId,
    baseUrl,
  ),
  queryFn: () =>
    envAgnostic
      ? getFileEnvAgnostic(workspaceBaseUrl!, fileId!)
      : filesApiBase(workspaceBaseUrl!).getFileEnvFilesFileIdGet({
          fileId: fileId!,
          env: environment!,
        }),
  enabled: Boolean(
    baseUrl &&
      fileId &&
      fileId !== PLACEHOLDER_FILE_ID &&
      (envAgnostic || environment),
  ),
  // Keep the presigned download_url fresh in the background so an idle case
  // tab never errors on open/download (ENR-17870). A file can only be opened
  // while the tab is focused, so two triggers fully cover it: refetch the
  // moment the user returns to a backgrounded tab (before they can click), and
  // poll on an interval shorter than the backend TTL while the tab stays open.
  // Consumers re-render with the new url; the expiry never surfaces as a 4xx.
  refetchOnWindowFocus: true,
  refetchInterval: FILE_URL_REFRESH_INTERVAL_MS,
  retry: (failureCount: number, error: Error) => {
    if (
      error instanceof AxiosError &&
      (error.response?.status === 404 || error.response?.status === 403)
    ) {
      return false;
    }
    return failureCount < 3;
  },
});

export const useFile = (
  fileId: string | undefined,
  environment: GetFileEnvFilesFileIdGetEnvEnum | undefined,
  baseUrl: string | undefined,
  options?: UseFileOptions,
) => {
  const { workspace } = useWorkspaceContext();

  return useQuery(
    fileQueryOptions({
      workspaceBaseUrl: workspace.base_url,
      fileId,
      environment,
      baseUrl,
      envAgnostic: options?.envAgnostic ?? false,
    }),
  );
};

const NO_FILES: FileMetadata[] = [];

export const useFiles = (
  fileIds: string[],
  environment: GetFilesEnvFilesGetEnvEnum | undefined,
): FileMetadata[] => {
  const { workspace } = useWorkspaceContext();

  const { data } = useQuery({
    queryKey: fileKeys.list(environment, fileIds, workspace.base_url),
    queryFn: () =>
      filesApiBase(workspace.base_url!).getFilesEnvFilesGet({
        env: environment!,
        filterId: fileIds.join(","),
      }),
    enabled: Boolean(workspace.base_url && environment && fileIds.length > 0),
  });

  return data?.data.data ?? NO_FILES;
};

export type DownloadFilesResult = { requested: number; downloaded: number };

export const useDownloadFiles = () => {
  const { workspace } = useWorkspaceContext();

  return useMutation<
    DownloadFilesResult,
    AxiosError,
    { fileIds: string[]; env: GetFilesEnvFilesGetEnvEnum }
  >({
    mutationFn: async ({ fileIds, env }) => {
      const filesApi = filesApiBase(workspace.base_url!);
      const response = await filesApi.getFilesEnvFilesGet({
        env,
        filterId: fileIds.join(","),
      });

      const downloadable = response.data.data.flatMap(({ attributes }) =>
        attributes.download_url
          ? [{ href: attributes.download_url, name: attributes.name }]
          : [],
      );

      const downloaded = await downloadable.reduce<Promise<number>>(
        (chain, file) =>
          chain.then(async (succeeded) => {
            try {
              await downloadFileFromUrl(file.href, file.name);
              return succeeded + 1;
            } catch {
              return succeeded;
            }
          }),
        Promise.resolve(0),
      );

      return { requested: fileIds.length, downloaded };
    },
    onSuccess: ({ requested, downloaded }) => {
      if (downloaded === 0) {
        toastActions.failure({ title: "Downloading the files failed" });
        return;
      }
      if (downloaded < requested) {
        toastActions.warning({
          title: `Downloaded ${downloaded} of ${requested} files`,
          description: "The remaining files could not be read.",
        });
        return;
      }
      toastActions.success({
        title: `Downloaded ${downloaded} ${downloaded === 1 ? "file" : "files"}`,
      });
    },
    onError: () => {
      toastActions.failure({ title: "Downloading the files failed" });
    },
  });
};

export type UseUploadFileReturn = {
  onFileSelect: (file: File, key: string) => void;
  onFileUpload: (
    file: File,
    key: string,
    env: UploadFileEnvFilesPostEnvEnum,
    fileSource: FileSource,
  ) => Promise<string | undefined>;
  onFileRemove: (key: string) => void;
  removeAllFiles: () => void;
  isLoading: boolean;
  getFile: (key: string) => File | undefined;
  getAllFiles: () => Map<string, File>;
  getFilesForField: (fieldKey: string) => Map<string, File>;
  updateFileName: (name: string, key: string) => void;
};

export const useUploadFile = (): UseUploadFileReturn => {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspaceContext();

  const { mutate: selectFile, isPending: isSelectFilePending } = useMutation({
    mutationFn: async ({
      file,
      name,
      key,
    }: {
      file: File;
      name?: string;
      key?: string;
    }) => {
      const renamedFile = name
        ? new File([file], name, { type: file.type })
        : file;
      queryClient.setQueryData(fileKeys.uploadedFile(key), renamedFile);
      return Promise.resolve(renamedFile);
    },
  });

  const { mutate: removeFile } = useMutation({
    mutationFn: async (key: string) => {
      queryClient.setQueryData(fileKeys.uploadedFile(key), null);
      return Promise.resolve();
    },
  });

  const removeAllFiles = useCallback(() => {
    queryClient.removeQueries({
      queryKey: FILES_QUERY_KEY,
    });
  }, [queryClient]);

  const getFile = useCallback(
    (key: string) => {
      const res = queryClient.getQueryData<File>(fileKeys.uploadedFile(key));
      return res;
    },
    [queryClient],
  );

  const getAllFiles = useCallback(() => {
    const files = new Map<string, File>();
    const queries = queryClient.getQueriesData<File>({
      queryKey: FILES_QUERY_KEY,
    });
    queries.forEach(([queryKey, file]) => {
      if (file && queryKey.length > 1) {
        files.set(queryKey[1] as string, file);
      }
    });
    return files;
  }, [queryClient]);

  // For multi-file fields ("files" type), each file is stored under a unique
  // key. This method retrieves all files belonging to a given field by prefix-matching on "fieldName::".
  const getFilesForField = useCallback(
    (fieldKey: string) => {
      const files = new Map<string, File>();
      const queries = queryClient.getQueriesData<File>({
        queryKey: FILES_QUERY_KEY,
      });
      const prefix = `${fieldKey}::`;
      queries.forEach(([queryKey, file]) => {
        if (file && queryKey.length > 1) {
          const key = queryKey[1] as string;
          if (key.startsWith(prefix)) {
            files.set(key, file);
          }
        }
      });
      return files;
    },
    [queryClient],
  );

  const { mutateAsync: uploadFile } = useMutation({
    mutationFn: async ({
      file,
      env,
      fileSource,
    }: {
      file: File;
      env: UploadFileEnvFilesPostEnvEnum;
      fileSource: FileSource;
    }) => {
      const filesApi = filesApiBase(workspace.base_url!);
      const fileAttributes: FileUploadRequest = {
        data: {
          type: "file_uploads" as FileUploadDataTypeEnum,
          attributes: {
            name: file.name,
          },
        },
      };
      const fileId = await filesApi.uploadFileEnvFilesPost({
        fileUploadRequest: fileAttributes,
        env: env,
        xFileSource: fileSource,
      });
      return Promise.resolve(fileId);
    },
  });

  const onFileSelect = useCallback(
    (file: File, key: string) => {
      selectFile({ file: file, key: key });
    },
    [selectFile],
  );

  const updateFileName = useCallback(
    (name: string, key: string) => {
      const currentFile = getFile(key);
      if (currentFile) {
        selectFile({ file: currentFile, name: name, key: key });
      }
    },
    [getFile, selectFile],
  );

  const onFileUpload = useCallback(
    async (
      file: File,
      key: string,
      env: UploadFileEnvFilesPostEnvEnum,
      fileSource: FileSource,
    ): Promise<string | undefined> => {
      const response = await uploadFile({
        file,
        env,
        fileSource,
      });
      const { upload_url, form_fields } = response.data?.data?.attributes || {};

      if (upload_url && form_fields) {
        const formData = new FormData();
        Object.entries(form_fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
        formData.append("file", file);

        await fetch(upload_url, {
          method: "POST",
          body: formData,
        });
      }

      return response.data?.data?.id;
    },
    [uploadFile],
  );

  const onFileRemove = useCallback(
    (key: string) => {
      removeFile(key);
    },
    [removeFile],
  );

  return {
    onFileSelect,
    onFileUpload,
    onFileRemove,
    removeAllFiles,
    isLoading: isSelectFilePending,
    getFile,
    getAllFiles,
    getFilesForField,
    updateFileName,
  };
};
